/**
 * Offline Authentication Service
 * Handles offline login using cached credentials stored in SQLite
 */

import { Capacitor } from '@capacitor/core';
import { getStorageJSON, setStorageJSON, removeStorageItem } from '../utils/capacitorStorage';

// Dynamic import for SQLite (only available on native platforms)
let CapacitorSQLite, SQLiteConnection, SQLiteDBConnection;
if (Capacitor.isNativePlatform()) {
  try {
    const sqliteModule = require('@capacitor-community/sqlite');
    CapacitorSQLite = sqliteModule.CapacitorSQLite;
    SQLiteConnection = sqliteModule.SQLiteConnection;
    SQLiteDBConnection = sqliteModule.SQLiteDBConnection;
  } catch (e) {
    console.warn('[OfflineAuth] SQLite plugin not available:', e);
  }
}

const DB_NAME = 'offline_auth_db';
const DB_VERSION = 1;

class OfflineAuthService {
  constructor() {
    this.db = null;
    this.isInitialized = false;
  }

  /**
   * Initialize SQLite database for offline auth
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      // Check if running on native platform
      if (!Capacitor.isNativePlatform()) {
        console.log('[OfflineAuth] Not running on native platform, using Capacitor Preferences fallback');
        this.isInitialized = true;
        return;
      }

      if (!CapacitorSQLite || !SQLiteConnection) {
        console.warn('[OfflineAuth] SQLite plugin not available, using Capacitor Preferences fallback');
        this.isInitialized = true;
        return;
      }

      // Initialize SQLite
      const sqlite = CapacitorSQLite;
      const sqliteConnection = new SQLiteConnection(sqlite);

      // Check if connection exists
      const isConnected = await sqliteConnection.isConnection(DB_NAME, false);
      if (isConnected.result) {
        this.db = await sqliteConnection.retrieveConnection(DB_NAME, false);
      } else {
        // Open or create database
        this.db = await sqliteConnection.createConnection(
          DB_NAME,
          false, // encrypted
          'no-encryption',
          DB_VERSION
        );
      }

      await this.db.open();

      // Create tables
      await this.createTables();

      this.isInitialized = true;
      console.log('[OfflineAuth] Database initialized successfully');
    } catch (error) {
      console.error('[OfflineAuth] Initialization error:', error);
      // Fallback to Capacitor Preferences
      this.isInitialized = true;
    }
  }

  /**
   * Create database tables
   */
  async createTables() {
    if (!this.db) return;

    const createTablesSQL = `
      -- Cached operator credentials for offline login
      CREATE TABLE IF NOT EXISTS operator_cache (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        operator_code TEXT UNIQUE NOT NULL,
        operator_id INTEGER NOT NULL,
        name TEXT NOT NULL,
        pin_hash TEXT NOT NULL,
        operator_data TEXT NOT NULL,
        device_mac_address TEXT,
        device_fingerprint TEXT,
        last_login_at INTEGER,
        created_at INTEGER NOT NULL,
        is_active INTEGER DEFAULT 1
      );

      -- Offline login sessions
      CREATE TABLE IF NOT EXISTS offline_sessions (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        operator_code TEXT NOT NULL,
        session_token TEXT UNIQUE NOT NULL,
        created_at INTEGER NOT NULL,
        expires_at INTEGER NOT NULL,
        is_active INTEGER DEFAULT 1,
        FOREIGN KEY (operator_code) REFERENCES operator_cache(operator_code) ON DELETE CASCADE
      );

      -- Indexes
      CREATE INDEX IF NOT EXISTS idx_operator_cache_code ON operator_cache(operator_code);
      CREATE INDEX IF NOT EXISTS idx_offline_sessions_token ON offline_sessions(session_token);
      CREATE INDEX IF NOT EXISTS idx_offline_sessions_active ON offline_sessions(is_active, expires_at);
    `;

    await this.db.execute(createTablesSQL);
  }

  /**
   * Simple hash function for PIN (not cryptographically secure, but sufficient for offline validation)
   */
  hashPin(pin) {
    // Simple hash - in production, you might want to use a proper hashing library
    let hash = 0;
    for (let i = 0; i < pin.length; i++) {
      const char = pin.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }

  /**
   * Cache operator credentials after successful online login
   */
  async cacheOperatorCredentials(operator, pin, deviceId = {}) {
    await this.initialize();

    try {
      const operatorData = {
        id: operator.id,
        operator_code: operator.operator_code,
        name: operator.name,
        photo: operator.photo,
        roles: operator.roles || [],
      };

      const pinHash = this.hashPin(pin);
      const timestamp = Date.now();

      if (this.db) {
        // Store in SQLite
        await this.db.run(
          `INSERT OR REPLACE INTO operator_cache 
           (operator_code, operator_id, name, pin_hash, operator_data, device_mac_address, device_fingerprint, last_login_at, created_at, is_active)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 1)`,
          [
            operator.operator_code,
            operator.id,
            operator.name,
            pinHash,
            JSON.stringify(operatorData),
            deviceId.mac_address || null,
            deviceId.device_fingerprint || null,
            timestamp,
            timestamp
          ]
        );
      }

      // Also store in Capacitor Preferences for immediate access
      await setStorageJSON('operatorAuth', {
        operator_id: operator.operator_code,
        pin: pin, // Store actual PIN for API calls
      });
      await setStorageJSON('operatorData', operatorData);

      console.log('[OfflineAuth] Cached credentials for operator:', operator.operator_code);
    } catch (error) {
      console.error('[OfflineAuth] Error caching credentials:', error);
      throw error;
    }
  }

  /**
   * Verify offline login using cached credentials
   */
  async verifyOfflineLogin(operatorId, pin) {
    await this.initialize();

    try {
      const operatorCode = operatorId?.trim() || '';
      const pinHash = this.hashPin(pin);

      if (this.db) {
        // Check in SQLite
        const result = await this.db.query(
          `SELECT * FROM operator_cache 
           WHERE (operator_code = ? OR operator_id = ?) 
           AND pin_hash = ? 
           AND is_active = 1`,
          [operatorCode, operatorCode, pinHash]
        );

        if (result.values.length > 0) {
          const cached = result.values[0];
          const operatorData = JSON.parse(cached.operator_data);

          // Update last login time
          await this.db.run(
            `UPDATE operator_cache SET last_login_at = ? WHERE id = ?`,
            [Date.now(), cached.id]
          );

          // Create offline session
          const sessionToken = `offline_${Date.now()}_${Math.random().toString(36).substring(7)}`;
          const expiresAt = Date.now() + (24 * 60 * 60 * 1000); // 24 hours

          await this.db.run(
            `INSERT INTO offline_sessions (operator_code, session_token, created_at, expires_at, is_active)
             VALUES (?, ?, ?, ?, 1)`,
            [cached.operator_code, sessionToken, Date.now(), expiresAt]
          );

          // Store in Capacitor Preferences for API calls
          await setStorageJSON('operatorAuth', {
            operator_id: cached.operator_code,
            pin: pin, // Store actual PIN for when online
          });
          await setStorageJSON('operatorData', operatorData);

          console.log('[OfflineAuth] Offline login verified for:', cached.operator_code);
          return {
            success: true,
            operator: operatorData,
            sessionToken,
            isOffline: true
          };
        }
      }

      // Fallback: Check Capacitor Preferences
      try {
        const storedAuth = await getStorageJSON('operatorAuth');
        const storedOperator = await getStorageJSON('operatorData');

        if (storedAuth && storedOperator) {
          // Verify PIN matches (simple check - stored PIN should match)
          if (storedAuth.pin === pin && 
              (storedAuth.operator_id === operatorCode || storedOperator.operator_code === operatorCode)) {
            return {
              success: true,
              operator: storedOperator,
              sessionToken: `offline_${Date.now()}`,
              isOffline: true
            };
          }
        }
      } catch (e) {
        console.error('[OfflineAuth] Fallback check error:', e);
      }

      return {
        success: false,
        error: 'Invalid credentials or operator not cached for offline access'
      };
    } catch (error) {
      console.error('[OfflineAuth] Error verifying offline login:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get cached operator data
   */
  async getCachedOperator(operatorCode) {
    await this.initialize();

    try {
      if (this.db) {
        const result = await this.db.query(
          `SELECT operator_data FROM operator_cache 
           WHERE operator_code = ? AND is_active = 1`,
          [operatorCode]
        );

        if (result.values.length > 0) {
          return JSON.parse(result.values[0].operator_data);
        }
      }

      // Fallback to Capacitor Preferences
      return await getStorageJSON('operatorData');
    } catch (error) {
      console.error('[OfflineAuth] Error getting cached operator:', error);
      return null;
    }
  }

  /**
   * Check if operator is cached for offline access
   */
  async isOperatorCached(operatorCode) {
    await this.initialize();

    try {
      if (this.db) {
        const result = await this.db.query(
          `SELECT COUNT(*) as count FROM operator_cache 
           WHERE operator_code = ? AND is_active = 1`,
          [operatorCode]
        );
        return (result.values[0]?.count || 0) > 0;
      }

      // Fallback check
      const stored = await getStorageJSON('operatorData');
      return stored && stored.operator_code === operatorCode;
    } catch (error) {
      console.error('[OfflineAuth] Error checking cache:', error);
      return false;
    }
  }

  /**
   * Clear cached credentials (on logout)
   */
  async clearCachedCredentials(operatorCode) {
    await this.initialize();

    try {
      if (this.db) {
        await this.db.run(
          `UPDATE operator_cache SET is_active = 0 WHERE operator_code = ?`,
          [operatorCode]
        );
        await this.db.run(
          `UPDATE offline_sessions SET is_active = 0 WHERE operator_code = ?`,
          [operatorCode]
        );
      }

      // Also clear Capacitor Preferences
      await removeStorageItem('operatorAuth');
      await removeStorageItem('operatorData');
    } catch (error) {
      console.error('[OfflineAuth] Error clearing cache:', error);
    }
  }

  /**
   * Get all cached operators (for admin/debugging)
   */
  async getAllCachedOperators() {
    await this.initialize();

    try {
      if (this.db) {
        const result = await this.db.query(
          `SELECT operator_code, name, last_login_at FROM operator_cache WHERE is_active = 1`
        );
        return result.values || [];
      }
      return [];
    } catch (error) {
      console.error('[OfflineAuth] Error getting cached operators:', error);
      return [];
    }
  }
}

// Export singleton instance
const offlineAuthService = new OfflineAuthService();
export default offlineAuthService;
